/**
 * Algolia Error Handler & Circuit Breaker
 * Handles edge cases, network failures, and Algolia outages
 * Provides graceful fallback to Shopify native search
 */

class AlgoliaErrorHandler {
  constructor() {
    // Circuit breaker state
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.failureThreshold = 3;
    this.timeout = 30000; // 30 seconds
    this.lastFailureTime = null;

    // Error tracking
    this.errors = [];
    this.maxErrors = 50;

    // Interval tracking for cleanup
    this.healthCheckIntervalId = null;

    // Initialize
    this.init();
  }

  /**
   * Initialize error handler
   */
  init() {
    // Set up global error handlers
    this.setupGlobalErrorHandlers();

    // Monitor Algolia health
    this.setupHealthMonitoring();

    // Set up fallback mechanisms
    this.setupFallbacks();

    this.log('Error handler initialized');
  }

  /**
   * Set up global error handlers
   */
  setupGlobalErrorHandlers() {
    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (this.isAlgoliaError(event.reason)) {
        this.handleError(event.reason);
        event.preventDefault();
      }
    });

    // Catch general errors
    window.addEventListener('error', (event) => {
      if (this.isAlgoliaError(event.error)) {
        this.handleError(event.error);
      }
    });

    // Intercept Algolia search calls
    this.interceptAlgoliaSearches();
  }

  /**
   * Check if error is Algolia-related
   */
  isAlgoliaError(error) {
    if (!error) return false;

    const errorString = error.toString().toLowerCase();
    return errorString.includes('algolia') ||
           errorString.includes('instantsearch') ||
           errorString.includes('algoliasearch');
  }

  /**
   * Handle error with circuit breaker pattern
   */
  handleError(error) {
    this.logError(error);

    this.failureCount++;
    this.lastFailureTime = Date.now();

    // Check if threshold reached
    if (this.failureCount >= this.failureThreshold) {
      this.openCircuit(error);
    }

    // Show user-friendly error message
    this.showErrorMessage(error);
  }

  /**
   * Log error for debugging
   */
  logError(error) {
    const errorLog = {
      timestamp: Date.now(),
      message: error.message || String(error),
      stack: error.stack,
      state: this.state,
      failureCount: this.failureCount
    };

    this.errors.push(errorLog);

    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    console.error('[Algolia Error]', error);

    // Send to analytics if available
    if (window.gtag) {
      gtag('event', 'exception', {
        description: errorLog.message,
        fatal: false
      });
    }
  }

  /**
   * Open circuit breaker
   */
  openCircuit(error) {
    this.state = 'OPEN';
    this.log('Circuit breaker OPENED - falling back to Shopify search');

    // Report to Sentry only when the breaker actually opens — i.e. a real,
    // user-facing search outage. Single transient failures (recovered by retry
    // or cache, with the circuit still CLOSED) are almost always client-side
    // network noise and were drowning the signal, so they're no longer reported.
    // A genuine Algolia outage trips many users' breakers at once and still spikes.
    if (error && window.Sentry?.captureException) {
      window.Sentry.captureException(error, {
        tags: {
          component: 'algolia-search',
          phase: 'runtime',
          circuit_state: this.state
        },
        contexts: {
          algolia: {
            failure_count: this.failureCount,
            failure_threshold: this.failureThreshold
          }
        },
        fingerprint: ['algolia', 'circuit-open']
      });
    }

    // Show notification
    this.showFallbackNotification();

    // Attempt to recover after timeout
    setTimeout(() => {
      this.state = 'HALF_OPEN';
      this.failureCount = 0;
      this.log('Circuit breaker HALF_OPEN - attempting recovery');
    }, this.timeout);
  }

  /**
   * Close circuit breaker
   */
  closeCircuit() {
    this.state = 'CLOSED';
    this.failureCount = 0;
    this.log('Circuit breaker CLOSED - Algolia restored');

    // Hide fallback notification
    this.hideFallbackNotification();
  }

  /**
   * Check if circuit is open
   */
  isCircuitOpen() {
    return this.state === 'OPEN';
  }

  /**
   * Intercept Algolia searches
   */
  interceptAlgoliaSearches() {
    // Wrap search execution
    const originalSearch = this.executeSearch;

    this.executeSearch = async (...args) => {
      if (this.isCircuitOpen()) {
        this.log('Circuit open - falling back to Shopify');
        return this.fallbackToShopify();
      }

      try {
        const result = await originalSearch.apply(this, args);
        this.onSuccess();
        return result;
      } catch (error) {
        this.handleError(error);
        return this.fallbackToShopify();
      }
    };
  }

  /**
   * Called on successful search
   */
  onSuccess() {
    // Reset failure count if in half-open state
    if (this.state === 'HALF_OPEN') {
      this.closeCircuit();
    }

    // Gradual recovery
    if (this.failureCount > 0) {
      this.failureCount = Math.max(0, this.failureCount - 1);
    }
  }

  /**
   * Fallback to Shopify native search
   */
  fallbackToShopify() {
    if (!window.algoliaConfig?.fallbackToShopify) {
      this.log('Fallback disabled');
      return;
    }

    this.log('Falling back to Shopify search');

    // Get current search query
    const searchInput = document.querySelector('input[name="q"], .search__input');
    const query = searchInput?.value || '';

    if (query) {
      // Redirect to Shopify search
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }
  }

  /**
   * Show error message to user
   */
  showErrorMessage(error) {
    // Check if error is user-facing
    if (this.isCriticalError(error)) {
      this.showNotification(
        'Search is temporarily unavailable. Please try again.',
        'error'
      );
    }
  }

  /**
   * Check if error is critical
   */
  isCriticalError(error) {
    const criticalErrors = [
      'network',
      'timeout',
      'failed to fetch',
      'api key',
      'index not found'
    ];

    const errorString = String(error).toLowerCase();
    return criticalErrors.some(critical => errorString.includes(critical));
  }

  /**
   * Show fallback notification
   */
  showFallbackNotification() {
    this.showNotification(
      'Search is using backup mode. Some features may be limited.',
      'warning'
    );
  }

  /**
   * Hide fallback notification
   */
  hideFallbackNotification() {
    const notification = document.querySelector('.algolia-fallback-notification');
    if (notification) {
      notification.remove();
    }
  }

  /**
   * Show notification to user
   */
  showNotification(message, type = 'info') {
    // Remove existing notifications
    const existing = document.querySelector('.algolia-notification');
    if (existing) {
      existing.remove();
    }

    // Create notification
    const notification = document.createElement('div');
    notification.className = `algolia-notification algolia-notification--${type}`;
    notification.innerHTML = `
      <div class="algolia-notification__content">
        <span class="algolia-notification__message">${message}</span>
        <button class="algolia-notification__close" aria-label="Close notification">
          ×
        </button>
      </div>
    `;

    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10000;
      padding: 1rem 1.5rem;
      background: ${type === 'error' ? '#fee' : type === 'warning' ? '#ffe' : '#eef'};
      border: 1px solid ${type === 'error' ? '#fcc' : type === 'warning' ? '#ffc' : '#ccf'};
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      max-width: 500px;
    `;

    // Add close handler
    notification.querySelector('.algolia-notification__close').addEventListener('click', () => {
      notification.remove();
    });

    // Auto-remove after 10 seconds
    setTimeout(() => {
      notification.remove();
    }, 10000);

    // Add to page
    document.body.appendChild(notification);
  }

  /**
   * Set up health monitoring
   */
  setupHealthMonitoring() {
    // Clear any existing interval to prevent duplicates
    if (this.healthCheckIntervalId) {
      clearInterval(this.healthCheckIntervalId);
    }

    // Check Algolia health every 60 seconds
    this.healthCheckIntervalId = setInterval(() => {
      if (this.state === 'OPEN' || this.state === 'HALF_OPEN') {
        this.checkHealth();
      }
    }, 60000);
  }

  /**
   * Clean up resources (call when destroying instance)
   */
  destroy() {
    if (this.healthCheckIntervalId) {
      clearInterval(this.healthCheckIntervalId);
      this.healthCheckIntervalId = null;
    }
    this.log('Error handler destroyed');
  }

  /**
   * Check Algolia health
   */
  async checkHealth() {
    if (!window.algoliaConfig) {
      return;
    }

    try {
      const searchClient = window.AlgoliaShared.excludeUnlisted(
        algoliasearch(window.algoliaConfig.appId, window.algoliaConfig.apiKey)
      );

      // Simple health check query
      await searchClient.search([{
        indexName: window.algoliaConfig.indexName,
        query: '',
        params: { hitsPerPage: 1 }
      }]);

      // Health check passed
      this.log('Health check passed');
      this.onSuccess();

    } catch (error) {
      this.log('Health check failed:', error);
    }
  }

  /**
   * Set up fallback mechanisms
   */
  setupFallbacks() {
    // Handle special characters
    this.setupSpecialCharacterHandling();

    // Handle empty results
    this.setupEmptyResultsHandling();

    // Handle slow network
    this.setupSlowNetworkHandling();
  }

  /**
   * Handle special characters in search
   */
  setupSpecialCharacterHandling() {
    // Sanitize search input
    document.addEventListener('input', (e) => {
      if (e.target.matches('input[name="q"], .search__input')) {
        const input = e.target;
        const value = input.value;

        // Allow most characters, just escape dangerous ones
        const sanitized = this.sanitizeInput(value);

        if (sanitized !== value) {
          input.value = sanitized;
        }
      }
    });
  }

  /**
   * Sanitize user input
   */
  sanitizeInput(input) {
    // Remove potential XSS vectors but keep useful characters
    return input
      .replace(/<script/gi, '')
      .replace(/<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '');
  }

  /**
   * Handle empty results
   */
  setupEmptyResultsHandling() {
    document.addEventListener('algolia:hits:rendered', () => {
      const adapter = window.algoliaAdapter || window.algoliaCollectionPage;

      if (!adapter || !adapter.search) {
        return;
      }

      const results = adapter.search.helper.lastResults;

      if (results && results.nbHits === 0) {
        this.showEmptyResultsSuggestions();
      }
    });
  }

  /**
   * Show suggestions for empty results
   */
  showEmptyResultsSuggestions() {
    const resultsContainer = document.querySelector('.search__results, .collection__content');

    if (!resultsContainer) {
      return;
    }

    // Add helpful message
    const emptyMessage = `
      <div class="algolia-empty-results">
        <h3>No products found</h3>
        <p>Try these suggestions:</p>
        <ul>
          <li>Check your spelling</li>
          <li>Try more general keywords</li>
          <li>Try different keywords</li>
        </ul>
        <p>
          <a href="/collections/all" class="btn">Browse all products</a>
        </p>
      </div>
    `;

    // Only add if not already present
    if (!resultsContainer.querySelector('.algolia-empty-results')) {
      resultsContainer.insertAdjacentHTML('beforeend', emptyMessage);
    }
  }

  /**
   * Handle slow network
   */
  setupSlowNetworkHandling() {
    // Show loading state for slow requests
    let loadingTimeout;

    document.addEventListener('algolia:search:started', () => {
      loadingTimeout = setTimeout(() => {
        this.showLoadingMessage();
      }, 2000); // Show after 2 seconds
    });

    document.addEventListener('algolia:hits:rendered', () => {
      clearTimeout(loadingTimeout);
      this.hideLoadingMessage();
    });
  }

  /**
   * Show loading message
   */
  showLoadingMessage() {
    const resultsContainer = document.querySelector('.search__results, .collection__content');

    if (!resultsContainer) {
      return;
    }

    const loadingMessage = document.createElement('div');
    loadingMessage.className = 'algolia-loading-message';
    loadingMessage.innerHTML = `
      <div style="text-align: center; padding: 2rem;">
        <p>Searching through ${(90000).toLocaleString()}+ products...</p>
        <div class="loading-spinner"></div>
      </div>
    `;

    resultsContainer.appendChild(loadingMessage);
  }

  /**
   * Hide loading message
   */
  hideLoadingMessage() {
    const loadingMessage = document.querySelector('.algolia-loading-message');
    if (loadingMessage) {
      loadingMessage.remove();
    }
  }

  /**
   * Get error report
   */
  getErrorReport() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      recentErrors: this.errors.slice(-10),
      totalErrors: this.errors.length
    };
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (window.algoliaConfig && window.algoliaConfig.debug) {
      // eslint-disable-next-line no-console
      console.log('[Algolia Error Handler]', ...args);
    }
  }
}

// Initialize error handler when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (window.algoliaConfig && window.algoliaConfig.enabled) {
    window.algoliaErrorHandler = new AlgoliaErrorHandler();
  }
});

// Make class available globally
window.AlgoliaErrorHandler = AlgoliaErrorHandler;
